'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { safeDecodeCollectionSlug } from '@/utils/collection-object'
import useSWRImmutable from 'swr/immutable'
import { API_URL, COLLECTIONS_VERSION } from '@/utils/constants'
import { fetcher } from '@/utils/fetcher'
import { formatSatsPer } from '@/utils/format-sats'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import formatNumber from '@/utils/format-number'
import { formatUsdPer, formatUsdPerBillion } from '@/utils/format-usd'
import BitcoinIcon from '@/icons/bitcoin-filled'
import { List, ExternalLink } from 'lucide-react'
import PercentChange from '@/components/percent-change'
import useEscrows from '@/utils/hooks/useEscrows'
import useSoldEscrows from '@/utils/hooks/useSoldEscrows'
import { useCollectionVWAPRange } from '@/utils/hooks/useCollectionVWAP'
import useCollectionMeta from '@/utils/hooks/useCollectionMeta'
import useTokenPrices from '@/utils/hooks/useTokenPrices'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { QuickViewBody, QuickViewMetric } from './collection-quickview'
import { NatOwnersPreview } from './nat-owners'

const NAT_DESCRIPTION = (
	<>
		NAT is the only censorship-resistant, no-fork-needed, working solution to Bitcoin&apos;s fading
		block reward and it&apos;s live today. Where to buy NAT? Only on Ordinals Wallet. Learn more at{' '}
		<a
			href="https://natgmi.com"
			target="_blank"
			rel="noopener noreferrer"
			className="text-[#6CE9A6] hover:underline"
		>
			natgmi.com
		</a>
		.
	</>
)

const formatBtc2 = (amountSats: number) => {
	if (!amountSats) return '0.00'
	const btc = amountSats / 1e8
	return btc.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const abbreviateNumber = (num: number) => {
	if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B'
	if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M'
	if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K'
	return num.toFixed(2)
}

interface NatInsightsProps {
	collection: any
	data: any
	slug: string
	/** Max pixel width for the embedded chart. */
	headerLeading?: React.ReactNode
	chartMaxWidth?: number
	onViewActivity?: () => void
	onViewOwners?: () => void
	/** Forwards the embedded chart's selected range up to the page header so
	 * the "7D Change" metric flips to "1D"/"1M"/etc. in sync with the chart. */
	onRangeChange?: (label: string, value: number) => void
}

const getLastWeekUnixRange = () => {
	const now = Math.floor(Date.now() / 1000)
	return { start: now - 7 * 86400, end: now }
}

/**
 * Builds the same QuickViewBody the "Insights" modal renders, but inline.
 * Used for the mobile Chart tab on tap-DMT-NAT.
 */
export function NatInsights({
	collection,
	data,
	slug,
	chartMaxWidth,
	headerLeading,
	onViewActivity,
	onViewOwners,
	onRangeChange
}: NatInsightsProps) {
	// Mirrors the embedded chart's selected range so the "Market Stats" grid's
	// Change row flips its label/value in step with the chart tabs (mobile was
	// stuck on "7D" because the metric label was hardcoded).
	const [chartRangeLocal, setChartRangeLocal] = useState<{ label: string; value: number } | null>(
		null
	)
	const handleRangeChange = useCallback(
		(label: string, value: number) => {
			setChartRangeLocal((prev) =>
				prev?.label === label && Math.abs(prev.value - value) < 0.000001 ? prev : { label, value }
			)
			onRangeChange?.(label, value)
		},
		[onRangeChange]
	)
	const { data: escrows } = useEscrows(slug)
	const { data: soldEscrows } = useSoldEscrows(slug)
	// Hourly VWAP for the last 7d — aligned with the chart's default 1W range
	// so the inline 7D Change metric matches the chart's percent change chip.
	const [natWeekRange] = useState(getLastWeekUnixRange)
	const { data: natHourlyVwap } = useCollectionVWAPRange(
		slug,
		'1h',
		natWeekRange.start,
		natWeekRange.end
	)
	const { data: collectionMeta } = useCollectionMeta(slug)
	const { data: tokenPrices } = useTokenPrices()
	const { exchangeRate } = useExchangeRate()
	const { data: homepageCollections } = useSWRImmutable(
		`${API_URL}/${COLLECTIONS_VERSION}?order=VolumeDayDesc&limit=1&search=${encodeURIComponent(
			slug
		)}`,
		fetcher
	)

	const natBestAskPerToken = useMemo(() => {
		if (escrows && Array.isArray(escrows) && escrows.length > 0) {
			const pricesPerToken = escrows
				.filter((e: any) => e.satoshi_price && e.amount)
				.map((e: any) => e.satoshi_price / parseFloat(e.amount || '1'))
			if (pricesPerToken.length > 0) return Math.min(...pricesPerToken)
		}
		return null
	}, [escrows])

	const natLastSalePerToken = useMemo(() => {
		if (soldEscrows && Array.isArray(soldEscrows) && soldEscrows.length > 0) {
			const sortedSold = [...soldEscrows]
				.filter((e: any) => e.satoshi_price && e.amount)
				.sort(
					(a: any, b: any) =>
						new Date(b.bought_at || b.sold_at || 0).getTime() -
						new Date(a.bought_at || a.sold_at || 0).getTime()
				)
			if (sortedSold.length > 0) {
				const lastSale = sortedSold[0]
				return lastSale.satoshi_price / parseFloat(lastSale.amount || '1')
			}
		}
		return null
	}, [soldEscrows])

	const tokenPrice = useMemo(() => {
		if (natBestAskPerToken !== null) return natBestAskPerToken
		if (natLastSalePerToken !== null) return natLastSalePerToken
		return parseFloat(
			(tokenPrices || []).find((e: any) => e.slug === safeDecodeCollectionSlug(slug))
				?.floor_price_per || collection?.floor_price_per
		)
	}, [slug, tokenPrices, collection, natBestAskPerToken, natLastSalePerToken])

	const { sats, per } = useMemo(() => formatSatsPer(tokenPrice), [tokenPrice])

	const listed = typeof escrows?.length === 'number' ? escrows.length : data?.listed || 0
	const sales = Math.max(soldEscrows?.length || 0, data?.sales || 0)
	const total_supply = collectionMeta?.total_supply ?? collection?.total_supply

	const homepageCollection = Array.isArray(homepageCollections?.collections)
		? homepageCollections.collections.find((c: any) => c?.slug === slug)
		: undefined
	const volume_total = (homepageCollection?.volume_all_time ?? data?.volume_total) || 0
	const volume_24h = (homepageCollection?.volume_day ?? data?.volume_day) || 0

	const natWeekChange = useMemo(() => {
		if (!Array.isArray(natHourlyVwap) || natHourlyVwap.length <= 1) return 0
		const hourly = natHourlyVwap
			.filter((d: any) => d && typeof d.time === 'number' && typeof d.vwap === 'number')
			.sort((a: any, b: any) => a.time - b.time)
		if (hourly.length === 0) return 0
		// Mirror chart.tsx 1W `visibleInfo`: first hourly bucket with
		// time ≥ (lastBucketTime − 7d) vs the last hourly bucket.
		const lastIdx = hourly.length - 1
		const lastTime = hourly[lastIdx].time
		const fromTime = lastTime - 7 * 24 * 60 * 60
		const foundIdx = hourly.findIndex((d: any) => d.time >= fromTime)
		const startIdx = foundIdx < 0 ? 0 : foundIdx
		const startVal = hourly[startIdx].vwap
		const endVal = hourly[lastIdx].vwap
		if (startVal > 0) return ((endVal - startVal) / startVal) * 100
		return 0
	}, [natHourlyVwap])

	const circulatingTokens =
		typeof data?.tap_circulating_supply === 'number'
			? data.tap_circulating_supply
			: typeof collection?.tap_circulating_supply === 'number'
			? collection.tap_circulating_supply
			: undefined
	const marketCap =
		typeof circulatingTokens === 'number' && tokenPrice ? circulatingTokens * tokenPrice : undefined

	const formatUSD = (satAmount: number) => {
		const usd = (satAmount / 1e8) * exchangeRate
		const formattedUsd = formatUsdPer(usd).usd
		return formattedUsd > 0 ? '$' + abbreviateNumber(formattedUsd) : null
	}

	const displayChangeLabel = chartRangeLocal?.label ?? '7D'
	const displayChangeValue =
		typeof chartRangeLocal?.value === 'number' ? chartRangeLocal.value : natWeekChange
	const meaningfulPriceUsd = tokenPrice
		? formatUsdPerBillion(tokenPrice * 1e-8 * exchangeRate)
		: null

	const metrics: QuickViewMetric[] = [
		{
			label: 'Market Cap',
			primary: typeof marketCap === 'number' ? formatBtc2(marketCap) : '0.00',
			secondary: typeof marketCap === 'number' ? formatUSD(marketCap) : null,
			isBtc: true
		},
		{
			label: tokenPrice
				? natBestAskPerToken !== null
					? 'Best Ask'
					: natLastSalePerToken !== null
					? 'Last Sale'
					: 'Reference Price'
				: 'Price',
			primary: tokenPrice ? (
				<span>
					{sats}
					<span className="ml-1 text-[11px] font-medium text-foreground/55">{per}</span>
				</span>
			) : (
				'0'
			),
			secondary: meaningfulPriceUsd,
			isBtc: false
		},
		{
			label: `${displayChangeLabel} VWAP Change`,
			primary: <PercentChange value={displayChangeValue} />,
			isBtc: false
		},
		{
			label: '24H Vol',
			primary: formatBtc2(volume_24h),
			secondary: volume_24h ? formatUSD(volume_24h) : null,
			isBtc: true
		},
		{
			label: 'Total Vol',
			primary: formatBtc2(volume_total),
			secondary: volume_total ? formatUSD(volume_total) : null,
			isBtc: true
		},
		{
			label: 'Sales',
			primary: sales.toLocaleString(),
			secondary: `${listed.toLocaleString()} listed`,
			isBtc: false
		},
		{
			label: 'Supply',
			primary:
				typeof circulatingTokens === 'number'
					? formatNumber(circulatingTokens)
					: typeof total_supply === 'number'
					? formatNumber(total_supply)
					: '—',
			isBtc: false
		}
	]

	// NAT per-token USD rounds to "$0.00" — render price-per-billion to
	// match the metrics row.
	const priceUsd = meaningfulPriceUsd
	const priceDisplay = tokenPrice ? (
		<span>
			{sats}
			<span className="ml-1 text-[13px] font-normal text-foreground/50">{per}</span>
		</span>
	) : (
		<span className="inline-flex items-center gap-2">
			{formatSatoshis(0)}
			<BitcoinIcon className="size-6" />
		</span>
	)

	return (
		<>
			<QuickViewBody
                headerLeading={headerLeading}
				collection={collection}
				slug={slug}
				metrics={metrics}
				description={NAT_DESCRIPTION}
				priceDisplay={priceDisplay}
				priceUsd={priceUsd}
				changeValue={displayChangeValue}
				changeLabel={displayChangeLabel}
				changeSource="Global VWAP"
				chartMaxWidth={chartMaxWidth}
				chartHeight={240}
				showChartAxes
				onRangeChange={handleRangeChange}
				metricsHeaderRight={
					onViewActivity ? (
						<button
							type="button"
							onClick={onViewActivity}
							className="inline-flex items-center gap-1 text-[10px] font-semibold text-foreground/70 hover:text-foreground"
						>
							<List size={12} />
							View activity
						</button>
					) : undefined
				}
			/>
			<NatOwnersPreview onViewAll={onViewOwners} />
			<a
				href="https://x.com/search?q=%24NAT"
				target="_blank"
				rel="noopener noreferrer"
				className="mx-5 mb-4 mt-2 flex items-center gap-4 rounded-xl bg-accent p-4 transition-colors hover:bg-accent/80"
			>
				<svg viewBox="0 0 24 24" className="size-6 shrink-0 fill-foreground">
					<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
				</svg>
				<div className="min-w-0 flex-1 border-l border-border pl-4">
					<div className="truncate text-base font-semibold text-foreground">
						What&apos;s Happening with NAT?
					</div>
					<div className="mt-0.5 flex items-center gap-1 whitespace-nowrap text-xs text-foreground/55">
						See what people are saying on X
						<ExternalLink size={10} />
					</div>
				</div>
			</a>
		</>
	)
}
